import { Link } from 'react-router-dom';
import type { Product } from '../lib/types';
import { categoryName } from '../config/brand';

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  return (
    <Link
      to={`/products/${encodeURIComponent(product.rowKey)}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-transparent transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:ring-brand-100"
    >
      {product.featured && (
        <span className="absolute left-3 top-3 z-10 rounded-full bg-accent-green px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white shadow-sm">
          Featured
        </span>
      )}

      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-contain p-5 transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="grid h-full place-items-center">
            <span className="font-display text-4xl font-bold text-slate-200">{product.rowKey}</span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-600">
          {categoryName(product.partitionKey)}
        </div>
        <h3 className="font-display text-base font-semibold leading-snug text-slate-900 md:text-lg">
          {product.name}
        </h3>
        {product.tagline && <p className="line-clamp-2 text-sm text-slate-600">{product.tagline}</p>}

        <div className="mt-auto flex items-end justify-between gap-2 pt-3">
          <div className="flex flex-wrap gap-1.5">
            {product.sizes.slice(0, 3).map((s) => (
              <span
                key={s}
                className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
              >
                {s}
              </span>
            ))}
          </div>
          <span
            className="translate-x-1 text-lg font-semibold text-brand-600 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
            aria-hidden="true"
          >
            &rarr;
          </span>
        </div>
      </div>
    </Link>
  );
}
